import { create } from 'zustand';
import { RESPONSE, REQUESTS } from '../../js/_constants';
import { apiGet, apiPatch } from '../../js/api';
import Server from '../../js/config/_server';
import api from '../../api';
import type { ResourceResponse } from '../../api/types/response';
import type { Device } from '../../api/types/index';
import useEditorialStore from './moduleTypeStore'; 

export type DevicesStateType = {
    loading: boolean;
    list: Device[];
    error: string | null;
    selected: Device;
    total: number;
};

type DevicePatchPayload = {
    id: Device['uuid'];
    body: Partial<Device>;
};

interface DeviceStore extends DevicesStateType {
    getDevice: (deviceUuid: string, brand: string) => Promise<void>;
    getDevices: () => Promise<void>;
    updateDevice: (payload: DevicePatchPayload) => Promise<void>;
    unAssignDevice: (deviceId: string, playlistID: string) => Promise<void>;
    setDevices: (devices: Device[]) => void;
}

const useDeviceStore = create<DeviceStore>()((set, get) => ({
    // Initial State
    loading: false,
    list: [],
    error: null,
    selected: {} as Device,
    total: 0,

    // Actions
    getDevice: async (deviceUuid, brand) => {
        set({ loading: true, error: null });
        try {
            const response = await api.getDevice(deviceUuid, brand);

            // Helper for parallel API calls
            const fetchNestedData = async (items: any[], key: string) => {
                const promises = items.map(item => 
                    apiGet(Server.baseUrl + item[key], { accept: REQUESTS.ACCEPT.JSON_LD })
                );
                const results = await Promise.all(promises);
                results.forEach((res, index) => { items[index][key] = res; });
            };

            // Process nested resources
            await Promise.all([
                fetchNestedData(response.playlists, 'contentPlaylist'),
                fetchNestedData(response.recentPlaylists, 'contentPlaylist'),
                // playlistGroups is an array of strings, handled slightly differently
                Promise.all(response.playlistGroups.map(url => 
                    apiGet(Server.baseUrl + url, { accept: REQUESTS.ACCEPT.JSON_LD })
                )).then(results => response.playlistGroups = results as any)
            ]);

            // Access moduleTypes from the other store
            const { moduleTypes } = useEditorialStore.getState();
            response.moduleTypes = moduleTypes.filter(module => 
                response.moduleTypes.includes(module[RESPONSE.ID])
            );

            // Dealership fetch
            const dealershipResponse = await apiGet(Server.baseUrl + response.dealership);
            response.dealership = dealershipResponse as any;

            set({ selected: response, loading: false });
        } catch (error) {
            set({ loading: false, error: 'There was an error loading the device.' });
        }
    },

    getDevices: async () => {
        set({ loading: true });
        try {
            const response = await api.getDevices();
            set({
                list: response[RESPONSE.EXPAND_COLLECTION],
                total: response[RESPONSE.TOTAL],
                loading: false
            });
        } catch (error) {
            set({ loading: false });
        }
    },

    updateDevice: async (payload: DevicePatchPayload) => {
        set({ loading: true });
        try {
            const { id, body } = payload;
            await api.updateDevice(id, body);
            // Refresh current device after update
            await get().getDevice(id.split('/').pop()!, id.split('/')[2]);
        } catch (error) {
            set({ loading: false });
            throw error;
        }
    },

    unAssignDevice: async (deviceId, playlistID) => {
        try {
            const response = await apiGet<ResourceResponse<Device>>(Server.baseUrl + deviceId);
            const url = `${Server.baseUrl}${response[RESPONSE.ID]}`;

            const playlists = response.playlists.filter(
                item => item.contentPlaylist !== playlistID
            );

            const options = {
                body: {
                    title: response.title,
                    description: response.description,
                    dealership: response.dealership.dealershipId,
                    playlists,
                    deviceClass: response.deviceClass,
                    moduleTypes: response.moduleTypes
                }
            };
            await apiPatch(url, options);
        } catch (error: any) {
            console.error('Error', error.message);
        }
    },

    setDevices: (devices) => set({ list: devices, loading: false })
}));

export default useDeviceStore;
